"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { importLibrary, setOptions } from "@googlemaps/js-api-loader";


import { getClubsApi } from "../api/ClientActions";

declare const google: any;

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

if (GOOGLE_MAPS_API_KEY) {
  setOptions({
    key: GOOGLE_MAPS_API_KEY,
    v: "weekly",
    language: "en",
    region: "IN",
  });
}

interface ClubMapItem {
  client_id?: number | string;
  id?: number | string;
  unique_slug?: string;
  client_name?: string;
  club_name?: string;
  organization_name?: string;
  name?: string;
  location_address?: string;
  latitude?: string | number | null;
  longitude?: string | number | null;
  contact_email?: string;
  client_email?: string;
  email?: string;
  mobile_number?: string;
  phone_number?: string;
  phone?: string;
  mobile?: string;
  level_name?: string;
  total_members?: number;
  member_count?: number;
  total_followers?: number;
  follower_count?: number;
  coords?: [number, number];
}

const escapeHtml = (value: any) => {
  return String(value || "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
};

const getClubName = (club: ClubMapItem) => {
  return (
    club?.client_name ||
    club?.club_name ||
    club?.organization_name ||
    club?.name ||
    "Club"
  );
};

const getClubSlug = (club: ClubMapItem) => {
  return String(club?.unique_slug || club?.client_id || club?.id || "").trim();
};

const getClubProfilePath = (club: ClubMapItem) => {
  const slug = getClubSlug(club);
  return slug ? `/club/${encodeURIComponent(slug)}` : "#";
};

const getClubCoords = (club: ClubMapItem): [number, number] | null => {
  const lat = club?.latitude;
  const lng = club?.longitude;

  if (
    lat === null ||
    lat === undefined ||
    lng === null ||
    lng === undefined ||
    lat === "" ||
    lng === ""
  ) {
    return null;
  }

  const numLat = Number(lat);
  const numLng = Number(lng);

  if (Number.isNaN(numLat) || Number.isNaN(numLng)) {
    return null;
  }

  return [numLat, numLng];
};

const PublicClubIndiaMap: React.FC = () => {
  const mapDivRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const infoWindowRef = useRef<any>(null);
  const initTimerRef = useRef<number | null>(null);
  const searchMarkerRef = useRef<any>(null);

  const [locationSearch, setLocationSearch] = useState("");
  const [searchingLocation, setSearchingLocation] = useState(false);
  const [locationSearchError, setLocationSearchError] = useState("");

  const [clients, setClients] = useState<ClubMapItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchClubs = async () => {
      try {
        setLoading(true);

        const result = await getClubsApi();

        if (cancelled) {
          return;
        }

        if (
          result?.success === false ||
          result?.status === false
        ) {
          setClients([]);
          return;
        }

        const clubList =
          Array.isArray(result?.data)
            ? result.data
            : Array.isArray(result?.clients)
            ? result.clients
            : [];

        setClients(clubList);
      } catch (error) {
        if (!cancelled) {
          console.error(
            "Club map fetch error:",
            error
          );

          setClients([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchClubs();

    return () => {
      cancelled = true;
    };
  }, []);

  const mapClubs = useMemo(() => {
    return (clients || [])
      .map((club: ClubMapItem) => ({
        ...club,
        coords: getClubCoords(club),
      }))
      .filter(
        (
          club
        ): club is ClubMapItem & {
          coords: [number, number];
        } => club.coords !== null
      );
  }, [clients]);

  useEffect(() => {
    const container = mapDivRef.current;

    if (!container || loading || !mapClubs.length || !GOOGLE_MAPS_API_KEY) {
      return undefined;
    }

    let cancelled = false;

    if (initTimerRef.current !== null) {
      window.clearTimeout(initTimerRef.current);
      initTimerRef.current = null;
    }

    const initializeGoogleMap = async () => {
      try {
        const mapsLibrary: any = await importLibrary("maps");
        const { Map } = mapsLibrary;

        if (cancelled || !mapDivRef.current) return;

        markersRef.current.forEach((marker) => marker.setMap(null));
        markersRef.current = [];

        if (infoWindowRef.current) {
          infoWindowRef.current.close();
        }

        const firstCoords = mapClubs[0]?.coords || [22.9734, 78.6569];

        const map = new Map(mapDivRef.current, {
          center: {
            lat: firstCoords[0],
            lng: firstCoords[1],
          },
          zoom: 6,
          minZoom: 2,
          maxZoom: 18,
          mapTypeControl: true,
          streetViewControl: true,
          fullscreenControl: true,
          zoomControl: true,
          gestureHandling: "cooperative",
        });

        mapInstanceRef.current = map;

        const infoWindow = new google.maps.InfoWindow();
        infoWindowRef.current = infoWindow;
        const bounds = new google.maps.LatLngBounds();

        mapClubs.forEach((club: ClubMapItem) => {
          if (!club.coords) return;

          const position = {
            lat: club.coords[0],
            lng: club.coords[1],
          };

          bounds.extend(position);

          const marker = new google.maps.Marker({
            map,
            position,
            title: getClubName(club),
          });

          const clubName = escapeHtml(getClubName(club));
          const category = escapeHtml(club?.level_name || "Club");
          const address = escapeHtml(
            club?.location_address || "Location not updated",
          );
          const email = escapeHtml(
            club?.contact_email || club?.client_email || club?.email,
          );
          const clubProfilePath = getClubProfilePath(club);

          marker.addListener("click", () => {
            infoWindow.setContent(`
              <div style="min-width:260px;max-width:320px;font-family:Inter,Arial,sans-serif;padding:4px 2px 2px">
                <div style="display:flex;align-items:flex-start;gap:10px;margin-bottom:10px">
                  <div style="width:42px;height:42px;min-width:42px;border-radius:14px;background:#eef4ff;color:#2563eb;display:flex;align-items:center;justify-content:center;font-size:18px;font-weight:700">
                    ${clubName.charAt(0).toUpperCase()}
                  </div>

                  <div style="min-width:0;flex:1">
                    <a
                      href="${clubProfilePath}"
                      style="display:block;margin:0 0 4px;font-size:16px;font-weight:700;color:#0f172a;text-decoration:none;line-height:1.25"
                    >
                      ${clubName}
                    </a>

                    <span style="display:inline-flex;background:#eef4ff;color:#2563eb;border-radius:999px;padding:4px 9px;font-size:11px;font-weight:600">
                      ${category}
                    </span>
                  </div>
                </div>

                <div style="background:#f8fafc;border:1px solid #e6edf7;border-radius:14px;padding:10px 12px;margin-bottom:10px">
                  <p style="margin:0;font-size:12px;color:#475569;line-height:1.45">
                    ${address}
                  </p>

                  ${
                    email
                      ? `<p style="margin:8px 0 0;font-size:12px;color:#475569;line-height:1.4">
                          <strong style="color:#334155">Email:</strong> ${email}
                        </p>`
                      : ""
                  }

                  <p style="margin:8px 0 0;font-size:12px;color:#475569;line-height:1.4">
                    <strong style="color:#334155">Members:</strong> ${
                      club?.member_count || club?.total_members || 0
                    }
                  </p>
                </div>

                <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px">
                  <a
                    href="${clubProfilePath}"
                    style="height:34px;border-radius:999px;background:#2563eb;color:#ffffff;font-size:12px;font-weight:700;text-decoration:none;display:flex;align-items:center;justify-content:center"
                  >
                    View Club
                  </a>

                  <a
                    href="https://www.google.com/maps/search/?api=1&query=${position.lat},${position.lng}"
                    target="_blank"
                    rel="noopener noreferrer"
                    style="height:34px;border-radius:999px;background:#ffffff;border:1px solid #bfdbfe;color:#2563eb;font-size:12px;font-weight:700;text-decoration:none;display:flex;align-items:center;justify-content:center"
                  >
                    Google Maps
                  </a>
                </div>
              </div>
            `);

            infoWindow.open({
              map,
              anchor: marker,
            });
          });

          markersRef.current.push(marker);
        });

        if (mapClubs.length > 1) {
          map.fitBounds(bounds, 50);

          google.maps.event.addListenerOnce(map, "idle", () => {
            if ((map.getZoom() || 0) > 9) {
              map.setZoom(9);
            }
          });
        } else {
          map.setCenter({
            lat: firstCoords[0],
            lng: firstCoords[1],
          });
          map.setZoom(10);
        }
      } catch (error) {
        console.error("Failed to load Google Maps:", error);
      }
    };

    initTimerRef.current = window.setTimeout(() => {
      initTimerRef.current = null;
      initializeGoogleMap();
    }, 100);

    return () => {
      cancelled = true;

      if (initTimerRef.current !== null) {
        window.clearTimeout(initTimerRef.current);
        initTimerRef.current = null;
      }

      markersRef.current.forEach((marker) => marker.setMap(null));
      markersRef.current = [];

      if (searchMarkerRef.current) {
        searchMarkerRef.current.setMap(null);
        searchMarkerRef.current = null;
      }

      if (infoWindowRef.current) {
        infoWindowRef.current.close();
        infoWindowRef.current = null;
      }

      mapInstanceRef.current = null;
    };
  }, [loading, mapClubs]);


  const handleLocationSearch = async () => {
    const query = locationSearch.trim();

    if (!query) {
      setLocationSearchError("Enter a location to search.");
      return;
    }

    if (!mapInstanceRef.current) {
      setLocationSearchError("Map is not ready yet.");
      return;
    }

    try {
      setSearchingLocation(true);
      setLocationSearchError("");

      const geocodingLibrary: any =
        await importLibrary("geocoding");
      const { Geocoder } = geocodingLibrary;

      const geocoder = new Geocoder();

      const response = await geocoder.geocode({
        address: query,
        componentRestrictions: {
          country: "IN",
        },
      });

      const result = response.results?.[0];

      if (!result?.geometry?.location) {
        setLocationSearchError("Location not found.");
        return;
      }

      const map = mapInstanceRef.current;
      const location = result.geometry.location;

      map.panTo(location);
      map.setZoom(14);

      if (searchMarkerRef.current) {
        searchMarkerRef.current.setMap(null);
      }

      searchMarkerRef.current = new google.maps.Marker({
        map,
        position: location,
        title: result.formatted_address || query,
        animation: google.maps.Animation.DROP,
      });
    } catch (error) {
      console.error("Location search failed:", error);
      setLocationSearchError("Unable to search this location.");
    } finally {
      setSearchingLocation(false);
    }
  };

  const handleLocationSearchKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleLocationSearch();
    }
  };

  return (
    <div className="public-clubs-map-page">
      <div className="public-clubs-map-shell">
        <header className="public-map-topbar">
          <a href="/" className="public-map-brand">
            <img
              src="/assets/logo.png"
              alt="Bharat Sangam"
              className="public-map-logo"
            />
          </a>

          <a href="/" className="public-map-home">
            <i className="feather-arrow-left" />
            Back to Home
          </a>
        </header>

        <section className="public-map-hero">
          <div className="public-map-hero-copy">
            <span className="public-map-eyebrow">
              <i className="feather-map-pin" />
              Bharat Sangam Club Network
            </span>

            <h2>Find Bharat Sangam Clubs Across India</h2>

            <p>
              Discover clubs, view their locations, and open directions directly
              from the map.
            </p>
          </div>

          <div className="public-map-stat">
            <strong>{clients.length}</strong>
            <span>Clubs</span>
          </div>
        </section>

        <section className="public-map-content">
          <div className="public-map-toolbar">
            <div className="public-map-search">
              <i className="feather-search" />

              <input
                type="text"
                value={locationSearch}
                onChange={(event) => {
                  setLocationSearch(event.target.value);
                  setLocationSearchError("");
                }}
                onKeyDown={handleLocationSearchKeyDown}
                placeholder="Search city, area or address"
                aria-label="Search map location"
              />

              {locationSearch && (
                <button
                  type="button"
                  className="public-map-clear"
                  onClick={() => {
                    setLocationSearch("");
                    setLocationSearchError("");

                    if (searchMarkerRef.current) {
                      searchMarkerRef.current.setMap(null);
                      searchMarkerRef.current = null;
                    }
                  }}
                  aria-label="Clear location search"
                >
                  <i className="feather-x" />
                </button>
              )}

              <button
                type="button"
                className="public-map-search-btn"
                onClick={handleLocationSearch}
                disabled={searchingLocation || !GOOGLE_MAPS_API_KEY}
              >
                {searchingLocation ? "Searching..." : "Search"}
              </button>
            </div>

            {locationSearchError && (
              <p className="public-map-search-error">{locationSearchError}</p>
            )}
          </div>

          <div className="public-map-heading-row">
            <div>
              <h2>Club Locations</h2>
              <p>Click a marker to view club information and directions.</p>
            </div>

            <span className="public-map-visible-count">
              {mapClubs.length} on map
            </span>
          </div>

          {!GOOGLE_MAPS_API_KEY ? (
            <div className="public-map-state">
              <div className="public-map-state-icon">
                <i className="feather-alert-circle" />
              </div>
              <h3>Google Maps API key is missing</h3>
              <p>
                Add REACT_APP_GOOGLE_MAPS_API_KEY to your .env file and restart
                the app.
              </p>
            </div>
          ) : loading ? (
            <div className="public-map-state">
              <div className="spinner-border text-primary" />
              <p>Loading clubs map...</p>
            </div>
          ) : mapClubs.length === 0 ? (
            <div className="public-map-state">
              <div className="public-map-state-icon">
                <i className="feather-map-pin" />
              </div>
              <h3>No club locations found</h3>
              <p>
                Clubs with valid latitude and longitude will appear here.
              </p>
            </div>
          ) : (
            <div className="public-map-frame">
              <div ref={mapDivRef} className="public-google-map" />
            </div>
          )}
        </section>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:wght@600;700&family=Inter:wght@400;500;600;700&display=swap');

        * {
          box-sizing: border-box;
        }

        .public-clubs-map-page {
          min-height: 100vh;
          padding: 0 20px 48px;
          font-family: 'Inter', sans-serif;
          color: #0f172a;
          background: #f6f8fc;
        }

        .public-clubs-map-shell {
          width: 100%;
          max-width: 1180px;
          margin: 0 auto;
        }

        /* TOP BAR */

        .public-map-topbar {
          height: 78px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
        }

        .public-map-brand {
          display: inline-flex;
          align-items: center;
          text-decoration: none;
        }

        .public-map-logo {
          width: 118px;
          height: auto;
          display: block;
        }

        .public-map-home {
          min-height: 36px;
          padding: 0 12px;
          border: 1px solid #e1e7f0;
          border-radius: 9px;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          color: #475569;
          background: #ffffff;
          font-size: 11px;
          font-weight: 700;
          text-decoration: none;
          transition: .2s ease;
        }

        .public-map-home:hover {
          color: #2F6FE4;
          border-color: #cbdaf3;
          background: #f8fbff;
          text-decoration: none;
        }

        /* HERO */

        .public-map-hero {
          min-height: 160px;
          padding: 26px 28px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 28px;
          border: 1px solid #e0e7f1;
          border-radius: 18px;
          background:
            linear-gradient(135deg, #ffffff 0%, #f7faff 72%, #eef4ff 100%);
        }

        .public-map-hero-copy {
          min-width: 0;
        }

        .public-map-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 10px;
          color: #2F6FE4;
          font-size: 10.5px;
          font-weight: 700;
        }

        .public-map-hero h1 {
          max-width: 720px;
          margin: 0;
          color: #0A2354;
          font-family: 'Fraunces', serif;
          font-size: clamp(28px, 4vw, 40px);
          font-weight: 700;
          line-height: 1.08;
          letter-spacing: -0.025em;
        }

        .public-map-hero p {
          max-width: 660px;
          margin: 10px 0 0;
          color: #748096;
          font-size: 13px;
          line-height: 1.6;
        }

        .public-map-stat {
          width: 96px;
          height: 96px;
          flex-shrink: 0;
          border: 1px solid #dbe5f4;
          border-radius: 18px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background: #ffffff;
        }

        .public-map-stat strong {
          color: #2F6FE4;
          font-size: 28px;
          font-weight: 700;
          line-height: 1;
        }

        .public-map-stat span {
          margin-top: 5px;
          color: #8791a2;
          font-size: 10px;
          font-weight: 700;
        }

        /* CONTENT */

        .public-map-content {
          margin-top: 16px;
          padding: 18px;
          border: 1px solid #e1e7ef;
          border-radius: 18px;
          background: #ffffff;
        }

        .public-map-toolbar {
          margin-bottom: 16px;
        }

        .public-map-search {
          position: relative;
          display: flex;
          align-items: center;
          width: 100%;
        }

        .public-map-search > i {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
          font-size: 14px;
          pointer-events: none;
          z-index: 2;
        }

        .public-map-search input {
          width: 100%;
          height: 44px;
          padding: 0 154px 0 38px;
          border: 1px solid #dfe5ee;
          border-radius: 10px;
          outline: none;
          color: #1f2937;
          background: #fbfcfe;
          font-size: 12.5px;
          transition: .2s ease;
        }

        .public-map-search input::placeholder {
          color: #9aa5b4;
        }

        .public-map-search input:focus {
          border-color: #2F6FE4;
          background: #ffffff;
          box-shadow: 0 0 0 3px rgba(47,111,228,.08);
        }

        .public-map-clear {
          position: absolute;
          right: 108px;
          top: 50%;
          width: 20px;
          height: 20px;
          padding: 0;
          border: 0;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transform: translateY(-50%);
          color: #64748b;
          background: #eef1f5;
          font-size: 10px;
          cursor: pointer;
          z-index: 3;
        }

        .public-map-search-btn {
          position: absolute;
          right: 5px;
          top: 5px;
          width: 96px;
          height: 34px;
          border: 0;
          border-radius: 8px;
          color: #ffffff;
          background: #2F6FE4;
          font-size: 11px;
          font-weight: 700;
          cursor: pointer;
          transition: .2s ease;
        }

        .public-map-search-btn:hover:not(:disabled) {
          background: #245ac0;
        }

        .public-map-search-btn:disabled {
          opacity: .6;
          cursor: not-allowed;
        }

        .public-map-search-error {
          margin: 6px 2px 0;
          color: #dc2626;
          font-size: 10.5px;
        }

        .public-map-heading-row {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 10px;
        }

        .public-map-heading-row h2 {
          margin: 0;
          color: #0A2354;
          font-family: 'Fraunces', serif;
          font-size: 18px;
          font-weight: 700;
        }

        .public-map-heading-row p {
          margin: 3px 0 0;
          color: #8a93a3;
          font-size: 11px;
        }

        .public-map-visible-count {
          min-height: 26px;
          padding: 0 9px;
          border: 1px solid #dde6f3;
          border-radius: 999px;
          display: inline-flex;
          align-items: center;
          color: #64748b;
          background: #f8fafc;
          font-size: 10px;
          font-weight: 700;
          white-space: nowrap;
        }

        /* MAP */

        .public-map-frame {
          width: 100%;
          height: 600px;
          min-height: 600px;
          overflow: hidden;
          border: 1px solid #dfe6ef;
          border-radius: 14px;
          background: #eef2f7;
        }

        .public-google-map {
          width: 100% !important;
          height: 600px !important;
          min-height: 600px !important;
          display: block !important;
        }

        /* STATES */

        .public-map-state {
          min-height: 420px;
          padding: 30px;
          border: 1px dashed #d8e0ea;
          border-radius: 14px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          background: #fbfcfe;
        }

        .public-map-state-icon {
          width: 46px;
          height: 46px;
          margin-bottom: 12px;
          border-radius: 13px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #2F6FE4;
          background: #eef4ff;
          font-size: 19px;
        }

        .public-map-state h3 {
          margin: 0;
          color: #334155;
          font-size: 14px;
          font-weight: 700;
        }

        .public-map-state p {
          max-width: 420px;
          margin: 6px 0 0;
          color: #94a3b8;
          font-size: 11.5px;
          line-height: 1.5;
        }

        .gm-style .gm-style-iw-c {
          border-radius: 14px !important;
          box-shadow: 0 16px 36px rgba(15,23,42,.16) !important;
        }

        @media (max-width: 768px) {
          .public-clubs-map-page {
            padding: 0 12px 32px;
          }

          .public-map-topbar {
            height: 66px;
          }

          .public-map-logo {
            width: 104px;
          }

          .public-map-hero {
            min-height: auto;
            padding: 18px;
            border-radius: 14px;
          }

          .public-map-hero h1 {
            font-size: 27px;
          }

          .public-map-stat {
            width: 78px;
            height: 78px;
            border-radius: 14px;
          }

          .public-map-stat strong {
            font-size: 23px;
          }

          .public-map-content {
            padding: 12px;
            border-radius: 14px;
          }

          .public-map-frame,
          .public-google-map {
            height: 470px !important;
            min-height: 470px !important;
          }
        }

        @media (max-width: 520px) {
          .public-map-home {
            padding: 0 10px;
          }

          .public-map-hero {
            align-items: flex-start;
          }

          .public-map-stat {
            width: 66px;
            height: 66px;
          }

          .public-map-stat strong {
            font-size: 20px;
          }

          .public-map-stat span {
            font-size: 9px;
          }

          .public-map-search {
            display: grid;
            grid-template-columns: minmax(0, 1fr) 82px;
            gap: 8px;
          }

          .public-map-search > i {
            left: 13px;
          }

          .public-map-search input {
            padding: 0 34px 0 36px;
          }

          .public-map-clear {
            right: 94px;
          }

          .public-map-search-btn {
            position: static;
            width: 82px;
            height: 44px;
          }

          .public-map-heading-row {
            align-items: flex-start;
          }

          .public-map-frame,
          .public-google-map {
            height: 410px !important;
            min-height: 410px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default PublicClubIndiaMap;
